import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useReactiveVar } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Checkbox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Toggle,
} from '@statsbomb/kitbag-components';
import { groupBy, sortBy } from 'lodash';
import { useTheme } from 'styled-components';
import { GET_GAMES } from '../Header/getGames';
import { ERROR_CODES } from '../../utils/errorCodes/errorCodes';
import {
  uiState,
  cf_GameIds,
  cf_GameIdsEnabled,
  mf_Teams,
  mf_Leagues,
} from '../../apollo';
import { enableFilter } from './Filters.helper';
import {
  TriggerButtonContainer,
  SelectAllButtonContainer,
  FilterRoundel,
} from './ContextFilters.styles';
import {
  PhaseContainer,
  CustomLabel,
  PhaseContainerFields,
} from './GamesFilter.styles';
import { useIsEligiblePage } from './ContextFilters.hooks';
import { API_COMPETITION_NAME } from '../../utils/constants/api';
import { FormField } from '../../styles/global.styles';

const GAME_PHASE_NAMES = {
  PLAYOFFS: 'Playoffs',
  POST_SEASON: 'Post-season',
};

function getGamePhaseDisplayName(phaseName, competitionId) {
  const isNcaaPlayoffs =
    competitionId === API_COMPETITION_NAME.NCAA &&
    phaseName === GAME_PHASE_NAMES.PLAYOFFS;

  return isNcaaPlayoffs ? GAME_PHASE_NAMES.POST_SEASON : phaseName;
}

function GamesFilter({ handleEnabledToggle }) {
  const theme = useTheme();
  // global state
  const teamId = useReactiveVar(mf_Teams);
  const competitionId = useReactiveVar(mf_Leagues);
  const cf_gamesEnabled = useReactiveVar(cf_GameIdsEnabled);
  const cf_gameIds = useReactiveVar(cf_GameIds);

  // local state
  const [gameIds, setGameIds] = useState(cf_gameIds);
  const [isOpen, setIsOpen] = useState(false);

  const { data, loading, error } = useQuery(GET_GAMES);
  if (error) {
    console.error(ERROR_CODES.GET_GAMES, error);
  }

  // sort the games from newest to to oldest and group them by phase
  const gamesData = data?.games.edges.map(({ node }) => node) || [];
  const sortedGames = sortBy(gamesData, (g) => new Date(g.date)).reverse();
  const groupedGames = groupBy(sortedGames, 'phaseName');
  const gamesEntries = Object.entries(groupedGames);

  const isEligiblePage = useIsEligiblePage('GAMES');
  const isEnabled = cf_gamesEnabled && isEligiblePage;

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    const { contextFilters } = uiState();

    contextFilters.gameIds.value = gameIds;
    cf_GameIds(gameIds);
    setIsOpen(false);
    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
  };

  const handleToggleGame = (id) => {
    if (gameIds.includes(id)) {
      setGameIds(gameIds.filter((g) => g !== id));
    } else {
      setGameIds([...gameIds, id]);
    }
    enableFilter(cf_gamesEnabled, cf_GameIdsEnabled, 'gameIds');
  };

  const handleSelectAll = (games) => {
    const ids = games.map((g) => g.id);
    setGameIds(ids);
  };

  const handleDeselectAll = () => {
    setGameIds([]);
  };

  const handleReset = () => {
    cf_GameIdsEnabled(false);
    setGameIds(cf_gameIds);
  };

  return (
    <>
      <TriggerButtonContainer>
        <Button
          variant={isEnabled ? 'primary' : 'secondary'}
          size="small"
          isDisabled={!isEligiblePage || loading}
          onClick={handleOpen}
        >
          Games
        </Button>
        {isEnabled && gamesData && (
          <FilterRoundel $opacity={loading ? theme.opacity.disabled : 1}>
            {gameIds?.length || '0'}
          </FilterRoundel>
        )}
      </TriggerButtonContainer>

      <Modal onCancel={handleClose} isOpen={isOpen}>
        <ModalHeader>Games</ModalHeader>
        <ModalBody>
          <div className="types">
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="game-type-toggle"
                label="Games"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_gamesEnabled,
                    cf_GameIdsEnabled,
                    'gameIds',
                    {
                      enabled: !cf_gamesEnabled,
                    },
                  )
                }
                checked={cf_gamesEnabled}
                size="small"
              />
            </FormField>
            <div>
              {gamesData &&
                gamesEntries.map(([phaseName, games], index) => (
                  <PhaseContainer key={phaseName}>
                    <h3
                      style={{
                        opacity: cf_gamesEnabled ? 1 : theme.opacity.disabled,
                      }}
                    >
                      {getGamePhaseDisplayName(phaseName, competitionId)}
                    </h3>
                    <PhaseContainerFields $numOfGames={games.length}>
                      {games.map((game) => {
                        const { homeTeam, awayTeam } = game;
                        const formattedDate = new Date(
                          game.date,
                        ).toLocaleString('en-us', {
                          timeZone: 'UTC',
                          day: 'numeric',
                          month: 'short',
                        });
                        // figure out which team is the selected team and which is the opponent
                        const isHomeTeam = homeTeam.id === teamId;
                        const selectedTeam = isHomeTeam ? homeTeam : awayTeam;
                        const opponentTeam = isHomeTeam ? awayTeam : homeTeam;

                        // use the data to assemble the checkbox label
                        const label = (
                          <CustomLabel htmlFor={game.id}>
                            <span className="round">{game.roundShortName}</span>
                            <span className="teams">
                              {`${selectedTeam.shortName} ${
                                isHomeTeam ? 'vs' : '@'
                              } ${opponentTeam.shortName}`}
                            </span>
                            <span className="date">{formattedDate}</span>
                          </CustomLabel>
                        );

                        return (
                          <FormField key={game.id}>
                            <Checkbox
                              id={game.id}
                              label={label}
                              onChange={() => handleToggleGame(game.id)}
                              checked={gameIds.includes(game.id)}
                              size="small"
                              disabled={!cf_gamesEnabled}
                            />
                          </FormField>
                        );
                      })}
                    </PhaseContainerFields>
                    <SelectAllButtonContainer>
                      <ButtonGroup>
                        <Button
                          size="small"
                          onClick={() => handleSelectAll(games)}
                          variant="secondary"
                          isDisabled={!cf_gamesEnabled}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleDeselectAll(games)}
                          variant="secondary"
                          isDisabled={!cf_gamesEnabled}
                        >
                          Deselect All
                        </Button>
                      </ButtonGroup>
                    </SelectAllButtonContainer>
                    {gamesEntries.length !== index + 1 && <hr />}
                  </PhaseContainer>
                ))}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleClose}>Save</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

GamesFilter.propTypes = {
  handleEnabledToggle: PropTypes.func.isRequired,
};

export default GamesFilter;
